import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  ChangeEvent,
  Dispatch,
  DragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { getMessageWindow, markChatRead, searchPatientChat } from '../api/chats';
import { fetchAuthenticatedBlob, fetchAuthenticatedResponse } from '../api/client';
import { useAdminRoster } from '../features/chats/hooks/useAdminRoster';
import { daySeparatorLabel, localDayKey } from '../features/chats/chatDate';
import { AppShell } from '../components/AppShell';
import { DatePicker } from '../components/DatePicker';
import { Modal } from '../components/ui/Modal';
import { Icon } from '../components/Icon';
import { AdminAvatarImage } from '../components/AdminAvatarImage';
import { PatientAvatarImage } from '../components/PatientAvatarImage';
import { Button, useActionDialog } from '../components/ui';
import {
  useAssignChatAdmins,
  useChatReaction,
  useDeleteChatMessage,
  useEditChatMessage,
  useMarkChatRead,
  useMarkChatUnread,
  useSendChatMessage,
  useSendFileMessage,
  useSendVoiceMessage,
} from '../features/chats/hooks/useChatMutations';
import { useAuth } from '../features/auth/AuthProvider';
import { useChats } from '../features/chats/hooks/useChats';
import { usePatientChat } from '../features/chats/hooks/usePatientChat';
import { useAdminChatRealtime } from '../features/chats/realtime/useAdminChatRealtime';
import { canDeleteMessage, canEditMessage } from '../features/chats/utils/chatPermissions';
import type {
  AdminChatListItem,
  ChatMessage,
  ChatSearchHit,
  ChatSearchResult,
  PatientChatThread,
} from '../types/admin';
import styles from './ChatsPage.module.scss';

type VoiceDraft = {
  readonly blob: Blob;
  readonly url: string;
  readonly durationMs: number;
  readonly filename: string;
};

type FileDraft = {
  readonly file: File;
  readonly filename: string;
  readonly sizeBytes: number;
};

const COMPOSER_FILE_ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt';

const COMPOSER_TEXTAREA_MAX_HEIGHT = 220;

const COMPOSER_ACCEPTED_EXTENSIONS = new Set(
  COMPOSER_FILE_ACCEPT.split(',').map((ext) => ext.trim().toLowerCase()),
);

const VOICE_PLAYBACK_RATES: readonly number[] = [1, 1.5, 2];
const CHAT_EMOJI = ['😀', '😂', '🥰', '😍', '😊', '😎', '😢', '😭', '😮', '😡', '👍', '👎', '❤️', '🔥', '🎉', '👏', '🙏', '💯', '🤍', '😃'];
const REACTION_EMOJI = ['👍', '❤️', '😂', '😮', '😢', '🔥', '👏', '🎉'];

function formatPlaybackRate(rate: number): string {
  return `${rate}×`;
}

function isComposerAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  const dot = name.lastIndexOf('.');
  if (dot < 0) {
    return false;
  }
  return COMPOSER_ACCEPTED_EXTENSIONS.has(name.slice(dot));
}

function dataTransferHasFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) {
    return false;
  }
  return Array.from(dataTransfer.types).includes('Files');
}

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function ComposerVoiceDraftRow({
  voiceDraft,
  onDiscard,
  onSend,
  isSendPending,
}: {
  readonly voiceDraft: VoiceDraft;
  readonly onDiscard: () => void;
  readonly onSend: () => void;
  readonly isSendPending: boolean;
}) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [previewStarted, setPreviewStarted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const onTimeUpdate = () => setPositionMs(audio.currentTime * 1000);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setPositionMs(0);
      setPreviewStarted(false);
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [voiceDraft.url]);

  useEffect(() => {
    setPlaying(false);
    setPositionMs(0);
    setPreviewStarted(false);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [voiceDraft.url]);

  function togglePlayback() {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (playing) {
      audio.pause();
      return;
    }
    setPreviewStarted(true);
    void audio.play().catch(() => {
      /* ignore autoplay / decode errors */
    });
  }

  const timeLabelMs =
    playing || previewStarted ? positionMs : voiceDraft.durationMs;

  return (
    <>
      <Button
        variant="ghost"
        iconOnly
        onClick={onDiscard}
        aria-label={t('chats.deleteVoiceMessage')}
        title={t('chats.discard')}
      >
        <Icon name="delete" size={18} />
      </Button>
      <div className={styles.voiceDraftPill}>
        <audio ref={audioRef} src={voiceDraft.url} preload="metadata" className={styles.voiceDraftAudioHidden} />
        <button
          type="button"
          className={styles.voiceDraftPlayChip}
          onClick={togglePlayback}
          aria-label={playing ? t('chats.pausePreview') : t('chats.playPreview')}
        >
          <Icon name={playing ? 'pause' : 'play'} size={16} />
          <span className={styles.voiceDraftTime}>{formatDuration(timeLabelMs)}</span>
        </button>
        <div className={styles.voiceDraftWaveformTrack} aria-hidden />
      </div>
      <Button
        variant="primary"
        iconOnly
        onClick={() => void onSend()}
        disabled={isSendPending}
        aria-label={t('chats.sendVoiceMessage')}
        title={t('chats.send')}
      >
        <Icon name="send" size={16} />
      </Button>
    </>
  );
}

export function ChatsPage() {
  const { t, i18n } = useTranslation();
  const { confirmAction } = useActionDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draft, setDraft] = useState('');
  const [composerEmojiOpen, setComposerEmojiOpen] = useState(false);
  const [reactionPickerId, setReactionPickerId] = useState<string | null>(null);
  const [reactionError, setReactionError] = useState<string | null>(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [selectedAdmins, setSelectedAdmins] = useState<readonly string[]>([]);
  const [voiceDraft, setVoiceDraft] = useState<VoiceDraft | null>(null);
  const [fileDraft, setFileDraft] = useState<FileDraft | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isComposerDragOver, setIsComposerDragOver] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingSessionRef = useRef(0);
  const { tokens, user } = useAuth();
  const [actionsMenu, setActionsMenu] = useState<{
    messageId: string;
    x: number;
    y: number;
  } | null>(null);
  const [chatMenu, setChatMenu] = useState<{ patientId: string; x: number; y: number } | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const messageStreamRef = useRef<HTMLDivElement | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const readRequestRef = useRef(false);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const queryClient = useQueryClient();

  // In-thread message search + keyset pagination state.
  const [isThreadSearchOpen, setIsThreadSearchOpen] = useState(false);
  const [activeHitId, setActiveHitId] = useState<string | null>(null);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const isAtBottomRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Telegram-style floating date pill + calendar jump-to-date.
  const [floatingDateLabel, setFloatingDateLabel] = useState<string | null>(null);
  const [isDatePillVisible, setIsDatePillVisible] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [jumpDateValue, setJumpDateValue] = useState('');
  const pillHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const el = composerTextareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, COMPOSER_TEXTAREA_MAX_HEIGHT);
    el.style.height = `${next}px`;
  }, [draft, editDraft, editingMessageId, voiceDraft, fileDraft, isRecording]);

  function formatDateTime(value: string | null): string {
    if (!value) return t('chats.noActivity');
    return new Date(value).toLocaleString(i18n.language, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const selectedPatientId = searchParams.get('patient') ?? '';
  const chatsQuery = useChats(debouncedSearch);
  const realtime = useAdminChatRealtime(chatsQuery.isSuccess, selectedPatientId);
  const filteredChats = chatsQuery.data ?? [];

  const isTypingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sendTypingRef = useRef(realtime.sendTyping);
  useEffect(() => {
    sendTypingRef.current = realtime.sendTyping;
  }, [realtime.sendTyping]);

  const cancelTypingTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const stopTyping = useCallback(
    (targetPatientId: string) => {
      cancelTypingTimers();
      if (isTypingRef.current && targetPatientId) {
        sendTypingRef.current(targetPatientId, false);
      }
      isTypingRef.current = false;
    },
    [cancelTypingTimers],
  );

  const handleDraftChange = useCallback(
    (value: string) => {
      setDraft(value);
      if (!selectedPatientId) {
        return;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        stopTyping(selectedPatientId);
        return;
      }
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        sendTypingRef.current(selectedPatientId, true);
        if (heartbeatTimerRef.current) {
          clearInterval(heartbeatTimerRef.current);
        }
        heartbeatTimerRef.current = setInterval(() => {
          if (isTypingRef.current) {
            sendTypingRef.current(selectedPatientId, true);
          }
        }, 3000);
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        stopTyping(selectedPatientId);
      }, 2000);
    },
    [selectedPatientId, stopTyping],
  );

  useEffect(() => {
    if (!selectedPatientId && filteredChats.length > 0) {
      const isDesktop =
        typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches;
      if (isDesktop) {
        setSearchParams({ patient: filteredChats[0]!.patient_id });
      }
    }
  }, [filteredChats, selectedPatientId, setSearchParams]);

  const threadQuery = usePatientChat(selectedPatientId);
  const lastMessageId =
    threadQuery.data?.messages[threadQuery.data.messages.length - 1]?.id ?? null;
  const messageCount = threadQuery.data?.messages.length ?? 0;
  const markRead = useMarkChatRead(selectedPatientId);
  const markUnread = useMarkChatUnread();
  const sendMessage = useSendChatMessage(selectedPatientId);
  const sendFileMessage = useSendFileMessage(selectedPatientId);
  const sendVoiceMessage = useSendVoiceMessage(selectedPatientId);
  const editMessageMutation = useEditChatMessage(selectedPatientId);
  const deleteMessageMutation = useDeleteChatMessage(selectedPatientId);
  const reactionMutation = useChatReaction(selectedPatientId);
  const assignAdmins = useAssignChatAdmins(selectedPatientId);
  const thread = threadQuery.data;
  const { data: rosterData } = useAdminRoster();
  const roster = rosterData ?? [];

  const markUnreadOnScrollDown = () => {
    if (!thread || thread.unread_count <= 0 || readRequestRef.current || markRead.isPending) {
      return;
    }
    readRequestRef.current = true;
    markRead.mutate(undefined, { onSettled: () => { readRequestRef.current = false; } });
  };

  useEffect(() => {
    readRequestRef.current = false;
    touchStartYRef.current = null;
  }, [selectedPatientId]);

  // Selecting a thread jumps to its latest message.
  useEffect(() => {
    isAtBottomRef.current = true;
    const node = messageStreamRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [selectedPatientId]);

  // New/changed messages only follow the tail if the user is already there, so
  // loading older pages or jumping to a search match is not yanked to bottom.
  useEffect(() => {
    const node = messageStreamRef.current;
    if (!node || !isAtBottomRef.current) {
      return;
    }
    node.scrollTop = node.scrollHeight;
  }, [lastMessageId, messageCount]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlderRef.current || !selectedPatientId || !tokens?.access_token) {
      return;
    }
    const current = queryClient.getQueryData<PatientChatThread>(['patient-chat', selectedPatientId]);
    const cursor = current?.page?.oldest_cursor;
    if (!current || !current.page?.has_more_before || !cursor) {
      return;
    }
    loadingOlderRef.current = true;
    const node = messageStreamRef.current;
    const prevScrollHeight = node?.scrollHeight ?? 0;
    const prevScrollTop = node?.scrollTop ?? 0;
    try {
      const older = await getMessageWindow(selectedPatientId, tokens.access_token, { before: cursor });
      const existingIds = new Set(current.messages.map((message) => message.id));
      const prepend = older.messages.filter((message) => !existingIds.has(message.id));
      queryClient.setQueryData<PatientChatThread>(['patient-chat', selectedPatientId], {
        ...current,
        messages: [...prepend, ...current.messages],
        page: {
          has_more_before: older.page.has_more_before,
          has_more_after: current.page?.has_more_after ?? false,
          oldest_cursor: older.page.oldest_cursor ?? current.page?.oldest_cursor ?? null,
          newest_cursor: current.page?.newest_cursor ?? null,
          page_size: current.page?.page_size ?? 30,
        },
      });
      // Restore the offset so prepending older messages keeps the viewport put.
      requestAnimationFrame(() => {
        const fresh = messageStreamRef.current;
        if (fresh) {
          fresh.scrollTop = prevScrollTop + (fresh.scrollHeight - prevScrollHeight);
        }
      });
    } finally {
      loadingOlderRef.current = false;
    }
  }, [queryClient, selectedPatientId, tokens]);

  const handleMessageStreamScroll = useCallback(() => {
    const node = messageStreamRef.current;
    if (!node) {
      return;
    }
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    const atBottom = distanceFromBottom <= 60;
    isAtBottomRef.current = atBottom;
    const current = queryClient.getQueryData<PatientChatThread>(['patient-chat', selectedPatientId]);
    setShowJumpToLatest(!atBottom || (current?.page?.has_more_after ?? false));
    if (node.scrollTop <= 120 && (current?.page?.has_more_before ?? false)) {
      void loadOlderMessages();
    }

    // Reflect the topmost visible day in the floating pill, then fade it out
    // shortly after the scroll stops.
    const containerTop = node.getBoundingClientRect().top;
    let label: string | null = null;
    for (const message of current?.messages ?? []) {
      const el = messageRefs.current.get(message.id);
      if (!el) {
        continue;
      }
      if (el.getBoundingClientRect().bottom > containerTop + 8) {
        label = daySeparatorLabel(message.created_at, i18n.language, t);
        break;
      }
    }
    if (label) {
      setFloatingDateLabel(label);
      setIsDatePillVisible(true);
    }
    if (pillHideTimerRef.current) {
      clearTimeout(pillHideTimerRef.current);
    }
    pillHideTimerRef.current = setTimeout(() => setIsDatePillVisible(false), 1200);
  }, [i18n.language, loadOlderMessages, queryClient, selectedPatientId, t]);

  useEffect(
    () => () => {
      if (pillHideTimerRef.current) {
        clearTimeout(pillHideTimerRef.current);
      }
    },
    [],
  );

  const handleJumpToHit = useCallback(
    async (hit: ChatSearchHit) => {
      if (!selectedPatientId || !tokens?.access_token) {
        return;
      }
      const current = queryClient.getQueryData<PatientChatThread>(['patient-chat', selectedPatientId]);
      if (!current) {
        return;
      }
      try {
        const windowPage = await getMessageWindow(selectedPatientId, tokens.access_token, {
          around: hit.id,
        });
        queryClient.setQueryData<PatientChatThread>(['patient-chat', selectedPatientId], {
          ...current,
          messages: windowPage.messages,
          page: windowPage.page,
        });
        setActiveHitId(hit.id);
        isAtBottomRef.current = false;
        requestAnimationFrame(() => {
          messageRefs.current.get(hit.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } catch {
        /* message no longer available — leave the current window in place */
      }
    },
    [queryClient, selectedPatientId, tokens],
  );

  const handleJumpToLatest = useCallback(async () => {
    if (!selectedPatientId || !tokens?.access_token) {
      return;
    }
    const current = queryClient.getQueryData<PatientChatThread>(['patient-chat', selectedPatientId]);
    const windowPage = await getMessageWindow(selectedPatientId, tokens.access_token, {});
    if (current) {
      queryClient.setQueryData<PatientChatThread>(['patient-chat', selectedPatientId], {
        ...current,
        messages: windowPage.messages,
        page: windowPage.page,
      });
    }
    setActiveHitId(null);
    isAtBottomRef.current = true;
    setShowJumpToLatest(false);
    setReactionPickerId(null);
    setComposerEmojiOpen(false);
    setReactionError(null);
    requestAnimationFrame(() => {
      const node = messageStreamRef.current;
      if (node) {
        node.scrollTop = node.scrollHeight;
      }
    });
  }, [queryClient, selectedPatientId, tokens]);

  const handleJumpToDate = useCallback(
    async (ymd: string) => {
      setJumpDateValue(ymd);
      setIsDatePickerOpen(false);
      if (!ymd || !selectedPatientId || !tokens?.access_token) {
        return;
      }
      const current = queryClient.getQueryData<PatientChatThread>(['patient-chat', selectedPatientId]);
      if (!current) {
        return;
      }
      const start = new Date(`${ymd}T00:00:00`); // local midnight
      try {
        const windowPage = await getMessageWindow(selectedPatientId, tokens.access_token, {
          around_date: start.toISOString(),
        });
        queryClient.setQueryData<PatientChatThread>(['patient-chat', selectedPatientId], {
          ...current,
          messages: windowPage.messages,
          page: windowPage.page,
        });
        setActiveHitId(null);
        isAtBottomRef.current = false;
        // First message on/after the selected day, else the newest (date past tail).
        const target =
          windowPage.messages.find(
            (message) => new Date(message.created_at).getTime() >= start.getTime(),
          ) ?? windowPage.messages[windowPage.messages.length - 1];
        if (target) {
          requestAnimationFrame(() => {
            messageRefs.current.get(target.id)?.scrollIntoView({ block: 'center' });
          });
        }
      } catch {
        /* no messages for the selected date window — leave the view in place */
      }
    },
    [queryClient, selectedPatientId, tokens],
  );

  const runMessageSearch = useCallback(
    (query: string, before?: string): Promise<ChatSearchResult> => {
      if (!tokens?.access_token) {
        return Promise.resolve({ hits: [], total: 0, next_cursor: null, query });
      }
      return searchPatientChat(selectedPatientId, tokens.access_token, { q: query, before });
    },
    [selectedPatientId, tokens],
  );

  useEffect(() => {
    if (thread) {
      setSelectedAdmins(thread.assigned_admins.map((admin) => admin.admin_id));
    }
  }, [thread]);

  useEffect(() => {
    setRecordingError(null);
    recordingSessionRef.current += 1;
    cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: true });
    setIsRecording(false);
    recordingStartedAtRef.current = null;
    resetVoiceDraft(voiceDraft, setVoiceDraft);
    setFileDraft(null);
    setIsComposerDragOver(false);
    setIsThreadSearchOpen(false);
    setActiveHitId(null);
    setMessageSearchQuery('');
    setShowJumpToLatest(false);
    isAtBottomRef.current = true;
    loadingOlderRef.current = false;
    messageRefs.current.clear();
  }, [selectedPatientId]);

  useEffect(() => {
    const previousPatientId = selectedPatientId;
    return () => {
      stopTyping(previousPatientId);
    };
  }, [selectedPatientId, stopTyping]);

  useEffect(() => {
    if (!isRecording) {
      setRecordingElapsedMs(0);
      return;
    }
    const timer = window.setInterval(() => {
      if (recordingStartedAtRef.current == null) {
        return;
      }
      setRecordingElapsedMs(Date.now() - recordingStartedAtRef.current);
    }, 250);
    return () => window.clearInterval(timer);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      recordingSessionRef.current += 1;
      cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: true });
      if (voiceDraft) {
        URL.revokeObjectURL(voiceDraft.url);
      }
    };
  }, [voiceDraft]);

  function handleSelect(chat: AdminChatListItem) {
    setChatMenu(null);
    setSearchParams({ patient: chat.patient_id });
    setAssignmentOpen(false);
    if (chat.manually_unread && tokens?.access_token) {
      void markChatRead(chat.patient_id, tokens.access_token).then(() => Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', chat.patient_id] }),
      ])).catch(() => { /* Keep marker when request fails. */ });
    }
  }

  async function handleSend() {
    const next = draft.trim();
    if (!next) return;
    stopTyping(selectedPatientId);
    await sendMessage.mutateAsync(next);
    setDraft('');
    setComposerEmojiOpen(false);
  }

  function openChatMenu(patientId: string, x: number, y: number) {
    setChatMenu({
      patientId,
      x: Math.max(8, Math.min(x, window.innerWidth - 188)),
      y: Math.max(8, Math.min(y, window.innerHeight - 56)),
    });
  }

  useEffect(() => {
    if (!chatMenu) return;
    const close = () => setChatMenu(null);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [chatMenu]);

  function insertEmoji(emoji: string) {
    const textarea = composerTextareaRef.current;
    const start = textarea?.selectionStart ?? draft.length;
    const end = textarea?.selectionEnd ?? start;
    handleDraftChange(`${draft.slice(0, start)}${emoji}${draft.slice(end)}`);
    setComposerEmojiOpen(false);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  }

  async function handleReaction(message: ChatMessage, emoji: string) {
    if (reactionMutation.isPending) return;
    setReactionPickerId(null);
    setReactionError(null);
    try {
      await reactionMutation.mutateAsync({
        messageId: message.id,
        emoji,
        remove: Boolean(message.reactions?.some((reaction) => reaction.emoji === emoji && reaction.reacted_by_me)),
      });
    } catch {
      setReactionError(t('chats.reactionFailed', 'Could not update reaction. Try again.'));
    }
  }

  function handleStartEdit(message: ChatMessage) {
    setEditingMessageId(message.id);
    setEditDraft(message.message_text);
    setActionsMenu(null);
    resetVoiceDraft(voiceDraft, setVoiceDraft);
    setFileDraft(null);
    setRecordingError(null);
    if (isRecording) {
      recordingSessionRef.current += 1;
      cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: true });
      setIsRecording(false);
      recordingStartedAtRef.current = null;
    }
  }

  function handleCancelEdit() {
    setEditingMessageId(null);
    setEditDraft('');
  }

  async function handleSaveEdit(messageId: string) {
    const next = editDraft.trim();
    if (!next) {
      handleCancelEdit();
      return;
    }
    try {
      await editMessageMutation.mutateAsync({ messageId, messageText: next });
      handleCancelEdit();
    } catch {
      /* keep edit mode on error so the user can retry */
    }
  }

  async function handleDelete(messageId: string) {
    setActionsMenu(null);
    if (!(await confirmAction({
      message: t('chats.confirmDeleteMessage'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) {
      return;
    }
    try {
      await deleteMessageMutation.mutateAsync(messageId);
    } catch {
      /* swallow — error toast surfaced via mutation state */
    }
  }

  async function handleToggleRecording() {
    if (!selectedPatientId) {
      return;
    }
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setRecordingError(t('chats.micNotAvailable'));
      return;
    }

    try {
      setRecordingError(null);
      resetVoiceDraft(voiceDraft, setVoiceDraft);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const options = getRecorderOptions();
      const recorder = options ? new MediaRecorder(stream, options) : new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      const sessionId = recordingSessionRef.current + 1;
      recordingSessionRef.current = sessionId;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      recorder.onstop = () => {
        if (sessionId !== recordingSessionRef.current) {
          cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: false });
          return;
        }
        const durationMs = Math.max(1000, Date.now() - (recordingStartedAtRef.current ?? Date.now()));
        cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: false });
        recordingStartedAtRef.current = null;
        setIsRecording(false);
        if (chunks.length === 0) {
          setRecordingError(t('chats.noAudioCaptured'));
          return;
        }
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const nextDraft = {
          blob,
          url: URL.createObjectURL(blob),
          durationMs,
          filename: buildVoiceFilename(recorder.mimeType || blob.type),
        };
        setVoiceDraft(nextDraft);
      };
      recorder.onerror = () => {
        if (sessionId !== recordingSessionRef.current) {
          cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: false });
          return;
        }
        cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: false });
        recordingStartedAtRef.current = null;
        setIsRecording(false);
        setRecordingError(t('chats.recordingStopped'));
      };

      mediaRecorderRef.current = recorder;
      recordingStartedAtRef.current = Date.now();
      setIsRecording(true);
      recorder.start();
    } catch {
      cleanupRecorder(mediaRecorderRef, mediaStreamRef, { cancel: true });
      recordingStartedAtRef.current = null;
      setIsRecording(false);
      setRecordingError(t('chats.micPermissionDenied'));
    }
  }

  async function handleSendVoice() {
    if (!voiceDraft) {
      return;
    }
    await sendVoiceMessage.mutateAsync({
      file: voiceDraft.blob,
      duration_ms: voiceDraft.durationMs,
      filename: voiceDraft.filename,
    });
    resetVoiceDraft(voiceDraft, setVoiceDraft);
  }

  const canComposerAcceptFile =
    Boolean(selectedPatientId) && !sendFileMessage.isPending && !isRecording;

  function applyComposerPickedFile(file: File) {
    if (!isComposerAcceptedFile(file)) {
      return;
    }
    setFileDraft({
      file,
      filename: file.name,
      sizeBytes: file.size,
    });
    setRecordingError(null);
  }

  function handlePickFile() {
    fileInputRef.current?.click();
  }

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    applyComposerPickedFile(file);
  }

  function handleComposerDragEnter(event: DragEvent<HTMLDivElement>) {
    if (!canComposerAcceptFile || !dataTransferHasFiles(event.dataTransfer)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setIsComposerDragOver(true);
  }

  function handleComposerDragLeave(event: DragEvent<HTMLDivElement>) {
    const next = event.relatedTarget as Node | null;
    if (next && event.currentTarget.contains(next)) {
      return;
    }
    setIsComposerDragOver(false);
  }

  function handleComposerDragOver(event: DragEvent<HTMLDivElement>) {
    if (!canComposerAcceptFile || !dataTransferHasFiles(event.dataTransfer)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function handleComposerDrop(event: DragEvent<HTMLDivElement>) {
    setIsComposerDragOver(false);
    if (!canComposerAcceptFile) {
      return;
    }
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      applyComposerPickedFile(file);
    }
  }

  async function handleSendFile() {
    if (!fileDraft) {
      return;
    }
    await sendFileMessage.mutateAsync({
      file: fileDraft.file,
      filename: fileDraft.filename,
    });
    setFileDraft(null);
  }

  async function handleSaveAssignments() {
    await assignAdmins.mutateAsync(selectedAdmins);
    setAssignmentOpen(false);
  }

  function handleMessageContextMenu(
    event: ReactMouseEvent<HTMLDivElement>,
    message: ChatMessage,
    canShow: boolean,
  ) {
    event.preventDefault();
    setReactionPickerId(message.id);
    if (!canShow) return;
    setActionsMenu({ messageId: message.id, x: event.clientX, y: event.clientY });
  }

  useEffect(() => {
    if (!actionsMenu) {
      return;
    }
    const close = () => setActionsMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActionsMenu(null);
      }
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [actionsMenu]);

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }

  return (
    <AppShell>
      <div className={styles.page}>
        <div className={styles.workspace} data-selected={selectedPatientId ? 'true' : 'false'}>
          <aside className={styles.roster}>
            <div className={styles.rosterHeader}>
              <div className={styles.rosterTitleRow}>
                <h2 className={styles.rosterTitle}>{t('chats.assignedChats')}</h2>
                <span className={styles.countChip}>{filteredChats.length}</span>
              </div>
              <label className={styles.searchBox}>
                <Icon name="search" size={16} />
                <input
                  value={search}
                  onChange={handleSearchChange}
                  placeholder={t('chats.searchPlaceholder')}
                />
              </label>
            </div>
            <div className={styles.chatList}>
              {filteredChats.map((chat) => {
                const active = chat.patient_id === selectedPatientId;
                const cardClass = `${styles.chatCard} ${active ? styles.chatCardActive : ''}`.trim();
                return (
                  <div key={chat.patient_id} className={styles.chatCardWrap}>
                  <button
                    type="button"
                    className={cardClass}
                    onClick={() => handleSelect(chat)}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      openChatMenu(chat.patient_id, event.clientX, event.clientY);
                    }}
                  >
                    <span className={styles.avatar}>
                      <PatientAvatarImage
                        patientId={chat.patient_id}
                        avatarUrl={chat.patient_avatar_url}
                        alt={chat.patient_name}
                        fallback={<span>{getInitials(chat.patient_name)}</span>}
                      />
                    </span>
                    <div className={styles.chatBody}>
                      <span className={styles.patientName}>{chat.patient_name}</span>
                      <p className={styles.preview}>{chat.last_message_preview}</p>
                    </div>
                    <div className={styles.chatMeta}>
                      <span className={styles.timestamp}>
                        {formatDateTime(chat.last_message_at)}
                      </span>
                      {chat.unread_count > 0 ? (
                        <span className={styles.unreadBadge}>{chat.unread_count}</span>
                      ) : chat.manually_unread ? <span className={styles.unreadDot} aria-label={t('chats.markUnread')} /> : null}
                    </div>
                  </button>
                  <button
                    type="button"
                    className={styles.chatMenuTrigger}
                    aria-label={t('chats.chatActions', { name: chat.patient_name })}
                    aria-haspopup="menu"
                    aria-expanded={chatMenu?.patientId === chat.patient_id}
                    onClick={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      openChatMenu(chat.patient_id, rect.left, rect.bottom);
                    }}
                  >⋮</button>
                  </div>
                );
              })}
            </div>
          </aside>

          <section className={styles.thread}>
            {!selectedPatientId ? (
              <div className={styles.emptyState}>{t('chats.selectChat')}</div>
            ) : threadQuery.isLoading ? (
              <div className={styles.emptyState}>{t('chats.loadingConversation')}</div>
            ) : !thread ? (
              <div className={styles.emptyState}>{t('chats.noConversation')}</div>
            ) : (
              <>
                <div className={styles.threadHeader}>
                  <button
                    type="button"
                    className={styles.threadBackButton}
                    onClick={() => setSearchParams({})}
                    aria-label={t('chats.backToList')}
                    title={t('chats.backToList')}
                  >
                    <Icon name="arrow-back" size={20} />
                  </button>
                  <div className={styles.threadHeaderInfo}>
                    <h2 className={styles.threadTitle}>{thread.patient_name}</h2>
                    <div className={styles.threadMeta}>
                      {thread.patient_display_id || t('chats.noPatientId')} · {t('chats.assignedAdminsCount', { count: thread.assigned_admins.length })}
                    </div>
                  </div>
                  <div className={styles.threadActions}>
                    <Button
                      variant="ghost"
                      iconOnly
                      onClick={() => {
                        setIsThreadSearchOpen((open) => {
                          const next = !open;
                          if (!next) {
                            setActiveHitId(null);
                            setMessageSearchQuery('');
                          }
                          return next;
                        });
                      }}
                      aria-label={t('chats.searchMessagesTitle')}
                      title={t('chats.searchMessagesTitle')}
                    >
                      <Icon name="search" size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      leadingIcon={<Icon name="person" size={18} />}
                      onClick={() => navigate(`/clients/${thread.patient_id}`)}
                      aria-label={t('chats.openPatientProfile')}
                      title={t('chats.patientProfileTitle')}
                    >
                      {t('chats.patientProfileTitle')}
                    </Button>
                    <Button
                      variant="ghost"
                      leadingIcon={<Icon name="people" size={18} />}
                      onClick={() => setAssignmentOpen((value) => !value)}
                      aria-label={assignmentOpen ? t('chats.closeTeamAssignment') : t('chats.manageAssignedTeam')}
                      title={assignmentOpen ? t('chats.close') : t('chats.manageTeam')}
                    >
                      {assignmentOpen ? t('chats.close') : t('chats.manageTeam')}
                    </Button>
                  </div>
                </div>

                {isThreadSearchOpen ? (
                  <ChatThreadSearch
                    onSearch={runMessageSearch}
                    onJump={(hit) => void handleJumpToHit(hit)}
                    onQueryChange={setMessageSearchQuery}
                    onClose={() => {
                      setIsThreadSearchOpen(false);
                      setActiveHitId(null);
                      setMessageSearchQuery('');
                    }}
                  />
                ) : null}

                {assignmentOpen ? (
                  <div className={styles.assignmentPanel}>
                    <div className={styles.assignmentTitle}>{t('chats.assignedAdminsTitle')}</div>
                    <div className={styles.assignmentGrid}>
                      {roster.map((admin) => {
                        const checked = selectedAdmins.includes(admin.admin_id);
                        return (
                          <label key={admin.admin_id} className={styles.assignmentOption}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setSelectedAdmins((current) =>
                                  checked
                                    ? current.filter((id) => id !== admin.admin_id)
                                    : [...current, admin.admin_id],
                                )
                              }
                            />
                            <AdminAvatarImage
                              avatarUrl={admin.avatar_url}
                              alt={t('chats.adminAvatarAlt', { name: admin.full_name })}
                              className={styles.assignmentSwatch}
                              fallback={
                                <span
                                  className={styles.assignmentSwatch}
                                  style={{ backgroundColor: admin.accent_color }}
                                >
                                  {admin.initials}
                                </span>
                              }
                            />
                            <span>{admin.full_name}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className={styles.assignmentActions}>
                      <Button
                        variant="ghost"
                        onClick={() => setAssignmentOpen(false)}
                        aria-label={t('chats.cancel')}
                        title={t('chats.cancel')}
                      >
                        {t('chats.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        leadingIcon={<Icon name="check" size={16} />}
                        onClick={() => void handleSaveAssignments()}
                        aria-label={t('chats.saveAssignments')}
                        title={t('chats.send')}
                      >
                        {t('chats.saveAssignments')}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {thread.assigned_admins.length > 0 ? (
                  <div className={styles.assignedRow}>
                    {thread.assigned_admins.map((admin) => (
                      <div key={admin.admin_id} className={styles.assignedCard}>
                        <AdminAvatarImage
                          avatarUrl={admin.avatar_url}
                          alt={t('chats.adminAvatarAlt', { name: admin.full_name })}
                          className={styles.assignedAvatar}
                          fallback={
                            <span
                              className={styles.assignedAvatar}
                              style={{ backgroundColor: admin.accent_color }}
                            >
                              {admin.initials}
                            </span>
                          }
                        />
                        <div className={styles.assignedInfo}>
                          <span className={styles.assignedName}>{admin.full_name}</span>
                          <span className={styles.assignedStatus}>
                            {admin.is_online ? t('chats.available') : t('chats.offline')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div
                  className={styles.messageStream}
                  ref={messageStreamRef}
                  onScroll={handleMessageStreamScroll}
                  onWheel={(event) => {
                    if (event.deltaY > 0) markUnreadOnScrollDown();
                  }}
                  onTouchStart={(event) => {
                    touchStartYRef.current = event.touches[0]?.clientY ?? null;
                  }}
                  onTouchMove={(event) => {
                    const startY = touchStartYRef.current;
                    const currentY = event.touches[0]?.clientY;
                    if (startY != null && currentY != null && startY - currentY > 8) {
                      markUnreadOnScrollDown();
                      touchStartYRef.current = null;
                    }
                  }}
                  onTouchEnd={() => { touchStartYRef.current = null; }}
                >
                  <button
                    type="button"
                    className={[
                      styles.floatingDatePill,
                      isDatePillVisible && floatingDateLabel
                        ? ''
                        : styles.floatingDatePillHidden,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setIsDatePickerOpen(true)}
                    aria-label={t('chats.jumpToDate')}
                    title={t('chats.jumpToDate')}
                  >
                    {floatingDateLabel ?? ''}
                  </button>
                  {realtime.isPatientTyping(selectedPatientId) ? (
                    <div
                      className={styles.typingRow}
                      role="status"
                      aria-live="polite"
                      aria-label={t('chats.patientTyping')}
                      data-testid="patient-typing-indicator"
                    >
                      <div className={styles.typingBubble}>
                        <span className={styles.typingDots} aria-hidden>
                          <span className={styles.typingDot} />
                          <span className={styles.typingDot} />
                          <span className={styles.typingDot} />
                        </span>
                        <span>{t('chats.patientTyping')}</span>
                      </div>
                    </div>
                  ) : null}
                  {thread.messages.map((message, index) => {
                    const own = message.sender_role === 'admin';
                    const editable = canEditMessage(message, user?.id);
                    const deletable = canDeleteMessage(message, user?.id, true);
                    const canShowMenu = editable || deletable;
                    const isMedia = message.message_type === 'voice' || message.message_type === 'file';
                    const isCurrentAdmin = message.sender_user_id === user?.id;
                    const showAdminAvatar = message.sender_role === 'admin' && !isCurrentAdmin;
                    const showPatientAvatar = message.sender_role === 'patient';
                    const previous = index > 0 ? thread.messages[index - 1] : null;
                    const showDaySeparator =
                      !previous ||
                      localDayKey(previous.created_at) !== localDayKey(message.created_at);
                    return (
                      <Fragment key={message.id}>
                        {showDaySeparator ? (
                          <div className={styles.messageDateSeparator}>
                            <span>{daySeparatorLabel(message.created_at, i18n.language, t)}</span>
                          </div>
                        ) : null}
                      <div
                        ref={(el) => {
                          if (el) {
                            messageRefs.current.set(message.id, el);
                          } else {
                            messageRefs.current.delete(message.id);
                          }
                        }}
                        className={`${styles.messageRow} ${own ? styles.messageRowOwn : ''}`}
                      >
                        {showAdminAvatar ? (
                          <AdminAvatarImage
                            avatarUrl={message.sender_avatar_url}
                            alt={t('chats.adminAvatarAlt', { name: message.sender_name })}
                            className={styles.messageAdminAvatar}
                            fallback={
                              <span className={styles.messageAdminAvatarFallback} aria-hidden>
                                {getInitials(message.sender_name)}
                              </span>
                            }
                          />
                        ) : null}
                        {showPatientAvatar ? (
                          <PatientAvatarImage
                            patientId={thread.patient_id}
                            avatarUrl={thread.patient_avatar_url}
                            alt={thread.patient_name}
                            className={styles.messageAdminAvatar}
                            fallback={
                              <span className={styles.messageAdminAvatarFallback} aria-hidden>
                                {getInitials(message.sender_name || thread.patient_name)}
                              </span>
                            }
                          />
                        ) : null}
                        <div
                          className={[
                            styles.messageBubbleHost,
                            isMedia ? styles.messageBubbleHostMedia : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <div
                            className={[
                              styles.messageBubble,
                              own ? styles.messageBubbleOwn : '',
                              isMedia ? styles.messageBubbleMedia : '',
                              message.id === activeHitId ? styles.messageBubbleActiveHit : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onContextMenu={(event) =>
                              handleMessageContextMenu(event, message, canShowMenu)
                            }
                          >
                            <div
                              className={`${styles.messageAuthor} ${own ? styles.messageAuthorOwn : styles.messageAuthorIncoming}`}
                            >
                              {message.sender_name}
                            </div>
                            {message.message_type === 'voice' ? (
                              <VoiceMessageCard
                                message={message}
                                accessToken={tokens?.access_token ?? null}
                                isOwn={own}
                              />
                            ) : message.message_type === 'file' ? (
                              <FileMessageCard
                                message={message}
                                accessToken={tokens?.access_token ?? null}
                              />
                            ) : (
                              <div className={styles.messageText}>
                                <HighlightedMessageText
                                  text={message.message_text}
                                  query={isThreadSearchOpen ? messageSearchQuery : ''}
                                />
                              </div>
                            )}
                            <div className={styles.messageTime}>
                              <span>{formatDateTime(message.created_at)}</span>
                              {message.is_edited ? (
                                <span className={styles.messageEditedTag}>
                                  ({t('chats.editedSuffix')})
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className={styles.messageReactionBar}>
                            {message.reactions?.filter((reaction) => reaction.count > 0).map((reaction) => (
                              <button
                                key={reaction.emoji}
                                type="button"
                                className={`${styles.reactionChip} ${reaction.reacted_by_me ? styles.reactionChipOwn : ''}`}
                                onClick={() => void handleReaction(message, reaction.emoji)}
                                disabled={reactionMutation.isPending}
                                aria-label={`${reaction.emoji} ${reaction.count}; ${reaction.reacted_by_me ? t('chats.removeReaction', 'Remove reaction') : t('chats.addReaction', 'Add reaction')}`}
                                aria-pressed={reaction.reacted_by_me}
                              >
                                <span aria-hidden="true">{reaction.emoji}</span> {reaction.count}
                              </button>
                            ))}
                            <button
                              type="button"
                              className={styles.addReactionButton}
                              onClick={() => setReactionPickerId(reactionPickerId === message.id ? null : message.id)}
                              aria-label={t('chats.reactToMessage', 'React to message')}
                              aria-expanded={reactionPickerId === message.id}
                            >
                              <span aria-hidden="true">☺+</span>
                            </button>
                          </div>
                          {reactionPickerId === message.id ? (
                            <div className={styles.reactionPicker} role="group" aria-label={t('chats.chooseReaction', 'Choose reaction')} onKeyDown={(event) => { if (event.key === 'Escape') setReactionPickerId(null); }}>
                              {REACTION_EMOJI.map((emoji) => (
                                <button
                                  type="button"
                                  key={emoji}
                                  onClick={() => void handleReaction(message, emoji)}
                                  disabled={reactionMutation.isPending}
                                  aria-label={`${t('chats.reactWith', 'React with')} ${emoji}`}
                                >{emoji}</button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      </Fragment>
                    );
                  })}
                  {showJumpToLatest ? (
                    <button
                      type="button"
                      className={styles.jumpToLatestButton}
                      onClick={() => void handleJumpToLatest()}
                      aria-label={t('chats.jumpToLatest')}
                      title={t('chats.jumpToLatest')}
                    >
                      <Icon name="chevron-down" size={20} />
                    </button>
                  ) : null}
                </div>

                <div
                  className={`${styles.composer} ${isComposerDragOver && canComposerAcceptFile ? styles.composerDragOver : ''}`}
                  onDragEnter={handleComposerDragEnter}
                  onDragLeave={handleComposerDragLeave}
                  onDragOver={handleComposerDragOver}
                  onDrop={handleComposerDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept={COMPOSER_FILE_ACCEPT}
                    onChange={handleFileSelected}
                  />
                  {isComposerDragOver && canComposerAcceptFile ? (
                    <div
                      className={styles.composerDropOverlay}
                      aria-hidden
                      onDragOver={handleComposerDragOver}
                      onDrop={handleComposerDrop}
                    >
                      {t('chats.dropFileHere')}
                    </div>
                  ) : null}
                  {fileDraft ? (
                    <div className={styles.draftRow}>
                      <div className={styles.draftBody}>
                        <span className={styles.draftTitle}>{t('chats.fileAttachmentReady')}</span>
                        <span className={styles.draftMeta}>
                          {fileDraft.filename} · {formatFileSize(fileDraft.sizeBytes)}
                        </span>
                      </div>
                      <div className={styles.draftActions}>
                        <Button
                          variant="ghost"
                          iconOnly
                          onClick={() => setFileDraft(null)}
                          aria-label={t('chats.discardAttachment')}
                          title={t('chats.discard')}
                        >
                          <Icon name="close" size={16} />
                        </Button>
                        <Button
                          variant="primary"
                          iconOnly
                          onClick={() => void handleSendFile()}
                          disabled={sendFileMessage.isPending}
                          aria-label={t('chats.sendFile')}
                          title={t('chats.send')}
                        >
                          <Icon name="send" size={16} />
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  {isRecording ? (
                    <div className={styles.recordingBanner}>
                      <span className={styles.recordingDot} />
                      {t('chats.recordingVoice', { time: formatDuration(recordingElapsedMs) })}
                    </div>
                  ) : null}
                  {recordingError ? <div className={styles.recordingError}>{recordingError}</div> : null}
                  {reactionError ? <div className={styles.recordingError} role="alert">{reactionError}</div> : null}
                  {editingMessageId ? (() => {
                    const target = thread.messages.find((m) => m.id === editingMessageId);
                    if (!target) {
                      return null;
                    }
                    return (
                      <div className={styles.editBanner}>
                        <span className={styles.editBannerIcon} aria-hidden>
                          <Icon name="edit" size={16} />
                        </span>
                        <div className={styles.editBannerBody}>
                          <span className={styles.editBannerTitle}>{t('chats.editMessage')}</span>
                          <span className={styles.editBannerPreview}>{target.message_text}</span>
                        </div>
                        <Button
                          variant="ghost"
                          iconOnly
                          onClick={handleCancelEdit}
                          aria-label={t('chats.cancelEdit')}
                          title={t('chats.cancelEdit')}
                        >
                          <Icon name="close" size={16} />
                        </Button>
                      </div>
                    );
                  })() : null}
                  <div className={styles.composerBar}>
                    {editingMessageId ? (
                      <>
                        <textarea
                          ref={composerTextareaRef}
                          className={styles.composerTextarea}
                          value={editDraft}
                          onChange={(event) => setEditDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                              event.preventDefault();
                              handleCancelEdit();
                            } else if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              void handleSaveEdit(editingMessageId);
                            }
                          }}
                          placeholder={t('chats.editMessagePlaceholder')}
                          rows={1}
                          autoFocus
                        />
                        <div className={styles.composerTrailing}>
                          <Button
                            variant="primary"
                            iconOnly
                            onClick={() => void handleSaveEdit(editingMessageId)}
                            disabled={editMessageMutation.isPending || !editDraft.trim()}
                            aria-label={t('chats.saveEdit')}
                            title={t('chats.saveEdit')}
                          >
                            <Icon name="check" size={16} />
                          </Button>
                        </div>
                      </>
                    ) : voiceDraft ? (
                      <ComposerVoiceDraftRow
                        voiceDraft={voiceDraft}
                        onDiscard={() => resetVoiceDraft(voiceDraft, setVoiceDraft)}
                        onSend={() => void handleSendVoice()}
                        isSendPending={sendVoiceMessage.isPending}
                      />
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          iconOnly
                          onClick={handlePickFile}
                          disabled={
                            !selectedPatientId || sendFileMessage.isPending || isRecording
                          }
                          aria-label={t('chats.attachFile')}
                          title={t('chats.attachFile')}
                        >
                          <Icon name="attach" size={18} />
                        </Button>
                        <textarea
                          ref={composerTextareaRef}
                          className={styles.composerTextarea}
                          value={draft}
                          onChange={(event) => handleDraftChange(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              void handleSend();
                            }
                          }}
                          placeholder={t('chats.messagePlaceholder')}
                          rows={1}
                        />
                        <div className={styles.composerEmojiHost}>
                          <Button
                            variant="ghost"
                            iconOnly
                            onClick={() => setComposerEmojiOpen((open) => !open)}
                            aria-label={t('chats.chooseEmoji', 'Choose emoji')}
                            aria-expanded={composerEmojiOpen}
                            title={t('chats.chooseEmoji', 'Choose emoji')}
                          >
                            <span aria-hidden="true">☺</span>
                          </Button>
                          {composerEmojiOpen ? (
                            <div className={styles.composerEmojiPicker} role="group" aria-label={t('chats.chooseEmoji', 'Choose emoji')} onKeyDown={(event) => { if (event.key === 'Escape') setComposerEmojiOpen(false); }}>
                              {CHAT_EMOJI.map((emoji) => (
                                <button type="button" key={emoji} onClick={() => insertEmoji(emoji)} aria-label={`${t('chats.insertEmoji', 'Insert emoji')} ${emoji}`}>{emoji}</button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <div className={styles.composerTrailing}>
                          {isRecording ? (
                            <Button
                              variant="danger"
                              iconOnly
                              onClick={() => void handleToggleRecording()}
                              aria-label={t('chats.stopRecording')}
                              title={t('chats.stopRecording')}
                            >
                              <Icon name="stop" size={16} />
                            </Button>
                          ) : draft.trim().length > 0 ? (
                            <Button
                              variant="primary"
                              iconOnly
                              onClick={() => void handleSend()}
                              disabled={sendMessage.isPending}
                              aria-label={t('chats.sendMessage')}
                              title={t('chats.send')}
                            >
                              <Icon name="send" size={16} />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              iconOnly
                              onClick={() => void handleToggleRecording()}
                              disabled={!selectedPatientId || sendVoiceMessage.isPending}
                              aria-label={t('chats.recordVoice')}
                              title={t('chats.voiceMessage')}
                            >
                              <Icon name="mic" size={18} />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
        {chatMenu ? (
          <div
            className={styles.chatActionsMenu}
            role="menu"
            style={{ left: chatMenu.x, top: chatMenu.y }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              disabled={markUnread.isPending}
              onClick={() => {
                const patientId = chatMenu.patientId;
                setChatMenu(null);
                markUnread.mutate(patientId);
              }}
            >{t('chats.markUnread')}</button>
          </div>
        ) : null}
        {actionsMenu
          ? (() => {
              const target = thread?.messages.find((m) => m.id === actionsMenu.messageId);
              if (!target) {
                return null;
              }
              const editable = canEditMessage(target, user?.id);
              const deletable = canDeleteMessage(target, user?.id, true);
              if (!editable && !deletable) {
                return null;
              }
              return (
                <div
                  className={styles.messageActionsMenuFloating}
                  role="menu"
                  style={{ left: actionsMenu.x, top: actionsMenu.y }}
                  onMouseDown={(event) => event.stopPropagation()}
                  onContextMenu={(event) => event.preventDefault()}
                >
                  {editable ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.messageActionsItem}
                      onClick={() => handleStartEdit(target)}
                    >
                      <span>{t('chats.editMessage')}</span>
                    </button>
                  ) : null}
                  {deletable ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={`${styles.messageActionsItem} ${styles.messageActionsItemDanger}`}
                      onClick={() => void handleDelete(target.id)}
                      disabled={deleteMessageMutation.isPending}
                    >
                      <Icon name="delete" size={14} />
                      <span>{t('chats.deleteMessage')}</span>
                    </button>
                  ) : null}
                </div>
              );
            })()
          : null}
      </div>
      <Modal
        open={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        title={t('chats.jumpToDate')}
        subtitle={t('chats.selectDate')}
        size="sm"
        closeAriaLabel={t('common.close')}
      >
        <DatePicker
          value={jumpDateValue}
          onChange={(value) => void handleJumpToDate(value)}
          placeholder={t('chats.selectDate')}
        />
      </Modal>
    </AppShell>
  );
}

function ChatThreadSearch({
  onSearch,
  onJump,
  onClose,
  onQueryChange,
}: {
  readonly onSearch: (query: string, before?: string) => Promise<ChatSearchResult>;
  readonly onJump: (hit: ChatSearchHit) => void;
  readonly onClose: () => void;
  readonly onQueryChange: (query: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<readonly ChatSearchHit[]>([]);
  const [total, setTotal] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  const run = useCallback(
    async (trimmed: string) => {
      const seq = (seqRef.current += 1);
      setIsLoading(true);
      try {
        const result = await onSearch(trimmed);
        if (seq !== seqRef.current) {
          return;
        }
        setHits(result.hits);
        setTotal(result.total);
        setNextCursor(result.next_cursor);
        setActiveIndex(result.hits.length ? 0 : -1);
      } catch {
        if (seq === seqRef.current) {
          setHits([]);
          setTotal(0);
          setNextCursor(null);
          setActiveIndex(-1);
        }
      } finally {
        if (seq === seqRef.current) {
          setIsLoading(false);
        }
      }
    },
    [onSearch],
  );

  function handleChange(value: string) {
    setQuery(value);
    onQueryChange(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      seqRef.current += 1;
      setHits([]);
      setTotal(0);
      setNextCursor(null);
      setActiveIndex(-1);
      setIsLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => void run(trimmed), 300);
  }

  async function goNext() {
    if (!hits.length) {
      return;
    }
    let workingHits = hits;
    let index = activeIndex;
    if (index >= hits.length - 1 && nextCursor) {
      const result = await onSearch(query.trim(), nextCursor);
      const existing = new Set(hits.map((hit) => hit.id));
      workingHits = [...hits, ...result.hits.filter((hit) => !existing.has(hit.id))];
      setHits(workingHits);
      setNextCursor(result.next_cursor);
    }
    if (index < workingHits.length - 1) {
      index += 1;
      setActiveIndex(index);
      onJump(workingHits[index]!);
    }
  }

  function goPrev() {
    if (!hits.length || activeIndex <= 0) {
      return;
    }
    const index = activeIndex - 1;
    setActiveIndex(index);
    onJump(hits[index]!);
  }

  const hasQuery = query.trim().length >= 2;
  const counter = !hasQuery
    ? ''
    : isLoading && !hits.length
      ? '…'
      : total === 0
        ? t('chats.searchNoResults')
        : `${activeIndex + 1}/${total}`;

  return (
    <div className={styles.threadSearch}>
      <Icon name="search" size={16} />
      <input
        autoFocus
        className={styles.threadSearchInput}
        value={query}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void goNext();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
          }
        }}
        placeholder={t('chats.searchMessages')}
      />
      {counter ? <span className={styles.threadSearchCounter}>{counter}</span> : null}
      <Button
        variant="ghost"
        iconOnly
        onClick={() => goPrev()}
        disabled={!hits.length}
        aria-label={t('chats.searchPrev')}
        title={t('chats.searchPrev')}
      >
        <span className={styles.threadSearchNavUp}>
          <Icon name="chevron-down" size={16} />
        </span>
      </Button>
      <Button
        variant="ghost"
        iconOnly
        onClick={() => void goNext()}
        disabled={!hits.length}
        aria-label={t('chats.searchNext')}
        title={t('chats.searchNext')}
      >
        <Icon name="chevron-down" size={16} />
      </Button>
      <Button
        variant="ghost"
        iconOnly
        onClick={onClose}
        aria-label={t('chats.closeSearch')}
        title={t('chats.closeSearch')}
      >
        <Icon name="close" size={16} />
      </Button>
    </div>
  );
}

function HighlightedMessageText({
  text,
  query,
}: {
  readonly text: string;
  readonly query: string;
}) {
  const trimmed = query.trim();
  if (!trimmed || !text) {
    return <>{text}</>;
  }
  const lowerText = text.toLowerCase();
  const lowerQuery = trimmed.toLowerCase();
  const parts: (string | ReactElement)[] = [];
  let start = 0;
  let key = 0;
  let matchIndex = lowerText.indexOf(lowerQuery, start);
  while (matchIndex >= 0) {
    if (matchIndex > start) {
      parts.push(text.slice(start, matchIndex));
    }
    parts.push(
      <mark key={key} className={styles.messageHighlight}>
        {text.slice(matchIndex, matchIndex + trimmed.length)}
      </mark>,
    );
    key += 1;
    start = matchIndex + trimmed.length;
    matchIndex = lowerText.indexOf(lowerQuery, start);
  }
  parts.push(text.slice(start));
  return <>{parts}</>;
}

function buildVoiceWaveformHeights(
  waveform: readonly number[] | null,
  barCount: number,
  seed: string,
): number[] {
  if (waveform && waveform.length > 0) {
    const out: number[] = [];
    for (let i = 0; i < barCount; i += 1) {
      const srcIndex = Math.min(waveform.length - 1, Math.floor((i / Math.max(1, barCount - 1)) * (waveform.length - 1)));
      const v = waveform[srcIndex] ?? 0;
      out.push(Math.max(14, Math.min(100, v)));
    }
    return out;
  }
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h + seed.charCodeAt(i) * (i + 1)) % 997;
  }
  return Array.from({ length: barCount }, (_, i) => {
    const x = Math.sin(h * 0.1 + i * 1.73) * 0.5 + 0.5;
    return Math.round(22 + x * 58);
  });
}

function VoiceMessageCard({
  message,
  accessToken,
  isOwn: _isOwn,
}: {
  readonly message: ChatMessage;
  readonly accessToken: string | null;
  readonly isOwn: boolean;
}) {
  const { t } = useTranslation();
  const voiceAttachment = message.attachments.find((attachment) => attachment.attachment_type === 'voice');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const barHeights = useMemo(
    () => buildVoiceWaveformHeights(voiceAttachment?.waveform ?? null, 22, message.id),
    [message.id, voiceAttachment?.waveform],
  );

  const durationMs = voiceAttachment?.duration_ms ?? 0;
  const progress =
    durationMs > 0 && positionMs >= 0 ? Math.min(1, Math.max(0, positionMs / durationMs)) : 0;
  const activeBarCount = Math.round(progress * barHeights.length);

  useEffect(() => {
    if (!voiceAttachment) {
      return;
    }
    if (voiceAttachment.content_url.startsWith('mock://')) {
      setAudioUrl(null);
      setLoadError(null);
      setIsLoading(false);
      return;
    }
    if (!accessToken) {
      setLoadError(t('chats.voicePlaybackRequiresSession'));
      return;
    }

    let isCancelled = false;
    let nextUrl: string | null = null;
    setIsLoading(true);
    setAudioUrl(null);
    setLoadError(null);

    void fetchAuthenticatedBlob(voiceAttachment.content_url, accessToken)
      .then((blob) => {
        if (isCancelled) {
          return;
        }
        nextUrl = URL.createObjectURL(blob);
        setAudioUrl(nextUrl);
      })
      .catch(() => {
        if (!isCancelled) {
          setLoadError(t('chats.voicePlaybackUnavailable'));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
      if (nextUrl) {
        URL.revokeObjectURL(nextUrl);
      }
    };
  }, [accessToken, voiceAttachment?.content_url, voiceAttachment?.id]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }
    const onTimeUpdate = () => setPositionMs(audio.currentTime * 1000);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setPositionMs(0);
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl]);

  useEffect(() => {
    setPlaying(false);
    setPositionMs(0);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackRate;
    }
  }, [playbackRate, audioUrl]);

  function cyclePlaybackRate() {
    setPlaybackRate((current) => {
      const index = VOICE_PLAYBACK_RATES.indexOf(current);
      return VOICE_PLAYBACK_RATES[(index + 1) % VOICE_PLAYBACK_RATES.length] ?? 1;
    });
  }

  function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }
    if (playing) {
      audio.pause();
      return;
    }
    void audio.play().catch(() => {
      /* ignore */
    });
  }

  function seekToFraction(fraction: number) {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }
    const total =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : durationMs > 0
          ? durationMs / 1000
          : 0;
    if (total <= 0) {
      return;
    }
    const clamped = Math.min(1, Math.max(0, fraction));
    const nextTime = clamped * total;
    audio.currentTime = nextTime;
    setPositionMs(nextTime * 1000);
  }

  function handleWaveformPointer(event: ReactPointerEvent<HTMLDivElement>) {
    if (!audioUrl) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    seekToFraction((event.clientX - rect.left) / rect.width);
  }

  function handleWaveformKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (!audioUrl) {
      return;
    }
    const total =
      durationMs > 0
        ? durationMs / 1000
        : audioRef.current && Number.isFinite(audioRef.current.duration)
          ? audioRef.current.duration
          : 0;
    if (total <= 0) {
      return;
    }
    const stepFraction = 5 / total;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      seekToFraction(progress - stepFraction);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      seekToFraction(progress + stepFraction);
    } else if (event.key === 'Home') {
      event.preventDefault();
      seekToFraction(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      seekToFraction(1);
    }
  }

  if (!voiceAttachment) {
    return <div className={styles.messageText}>{t('chats.voiceMessageFallback')}</div>;
  }

  const mockMode = voiceAttachment.content_url.startsWith('mock://');
  const metaDurationMs = playing && durationMs > 0 ? positionMs : durationMs;
  const durationLabel = formatDuration(metaDurationMs);
  const sizeLabel = voiceAttachment.byte_size ? formatFileSize(voiceAttachment.byte_size) : null;
  const metaLabel = sizeLabel ? `${durationLabel}, ${sizeLabel}` : durationLabel;

  if (mockMode) {
    return (
      <div className={styles.voice}>
        <span className={styles.voicePlayHost} aria-hidden>
          <Icon name="play" size={16} />
        </span>
        <div className={styles.voiceContent}>
          <div className={styles.voiceWaveform} aria-hidden>
            {barHeights.map((h, i) => (
              <span
                key={i}
                className={`${styles.voiceBar} ${i < activeBarCount ? styles.voiceBarPlayed : ''}`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          <div className={styles.voiceMeta}>{metaLabel}</div>
          <div className={styles.voiceHint}>{t('chats.demoVoiceMock')}</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.voice}>
        <span className={styles.voicePlayHost} aria-hidden>
          <span className={styles.voiceSpinner} />
        </span>
        <div className={styles.voiceContent}>
          <div className={styles.voiceWaveform} aria-hidden>
            {barHeights.map((h, i) => (
              <span key={i} className={styles.voiceBar} style={{ height: `${h}%`, opacity: 0.35 }} />
            ))}
          </div>
          <div className={styles.voiceMeta}>{t('chats.voiceLoading')}</div>
        </div>
      </div>
    );
  }

  if (!audioUrl) {
    return (
      <div className={styles.voice}>
        <span className={styles.voicePlayHost} aria-hidden>
          <Icon name="play" size={16} />
        </span>
        <div className={styles.voiceContent}>
          <div className={styles.voiceWaveform} aria-hidden>
            {barHeights.map((h, i) => (
              <span key={i} className={styles.voiceBar} style={{ height: `${h}%`, opacity: 0.35 }} />
            ))}
          </div>
          <div className={styles.voiceMeta}>{loadError ?? t('chats.voicePlaybackUnavailable')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.voice}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" className={styles.voiceAudioHidden} />
      <button
        type="button"
        className={styles.voicePlayButton}
        onClick={togglePlayback}
        aria-label={playing ? t('chats.pauseVoiceMessage') : t('chats.playVoiceMessage')}
      >
        <Icon name={playing ? 'pause' : 'play'} size={18} />
      </button>
      <div className={styles.voiceContent}>
        <div
          className={`${styles.voiceWaveform} ${styles.voiceWaveformInteractive}`}
          role="slider"
          aria-label={t('chats.seekVoiceMessage')}
          aria-valuemin={0}
          aria-valuemax={Math.round((durationMs > 0 ? durationMs : 0) / 1000)}
          aria-valuenow={Math.round(positionMs / 1000)}
          tabIndex={0}
          onPointerDown={handleWaveformPointer}
          onKeyDown={handleWaveformKeyDown}
        >
          {barHeights.map((h, i) => (
            <span
              key={i}
              className={`${styles.voiceBar} ${i < activeBarCount ? styles.voiceBarPlayed : ''}`}
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
        <div className={styles.voiceMeta}>{metaLabel}</div>
      </div>
      <button
        type="button"
        className={styles.voiceSpeedButton}
        onClick={cyclePlaybackRate}
        aria-label={t('chats.playbackSpeed')}
        title={t('chats.playbackSpeed')}
      >
        {formatPlaybackRate(playbackRate)}
      </button>
    </div>
  );
}

function FileMessageCard({
  message,
  accessToken,
}: {
  readonly message: ChatMessage;
  readonly accessToken: string | null;
}) {
  const { t } = useTranslation();
  const fileAttachment = message.attachments.find((attachment) => attachment.attachment_type === 'file');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const contentUrl = fileAttachment?.content_url ?? null;
  const isImage = isImageAttachment(fileAttachment?.mime_type ?? null);
  const isMockAttachment = Boolean(contentUrl?.startsWith('mock://'));

  // Image attachments load an authenticated blob so they render inline instead
  // of as a bare filename card. Non-images, mocks, or fetch failures fall back
  // to the download card below.
  useEffect(() => {
    if (!isImage || !contentUrl || isMockAttachment || !accessToken) {
      return;
    }

    let isCancelled = false;
    let objectUrl: string | null = null;
    setPreviewFailed(false);

    void fetchAuthenticatedBlob(contentUrl, accessToken)
      .then((blob) => {
        if (isCancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!isCancelled) {
          setPreviewFailed(true);
        }
      });

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setPreviewUrl(null);
    };
  }, [accessToken, contentUrl, isImage, isMockAttachment]);

  if (!fileAttachment) {
    return <div className={styles.messageText}>{t('chats.fileAttachmentFallback')}</div>;
  }

  async function handleDownload() {
    const attachment = fileAttachment;
    if (!attachment || !accessToken || isDownloading) {
      return;
    }
    if (attachment.content_url.startsWith('mock://')) {
      setDownloadError(t('chats.mockFileAttachment'));
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetchAuthenticatedResponse(attachment.content_url, accessToken);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const filename =
        parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
        attachment.original_filename ??
        'chat-attachment';
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(t('chats.fileDownloadUnavailable'));
    } finally {
      setIsDownloading(false);
    }
  }

  const filename = fileAttachment.original_filename ?? 'chat-attachment';

  if (isImage && previewUrl && !previewFailed) {
    return (
      <div>
        <figure className={styles.imageCard}>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className={styles.imagePreviewLink}
            title={t('chats.openImage')}
          >
            <img className={styles.imagePreview} src={previewUrl} alt={filename} loading="lazy" />
          </a>
          <figcaption className={styles.imageCardFooter}>
            <span className={styles.fileMeta}>{formatFileSize(fileAttachment.byte_size)}</span>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={() => void handleDownload()}
              disabled={!accessToken || isDownloading}
              aria-label={isDownloading ? t('chats.downloading') : t('chats.downloadFile')}
              title={isDownloading ? t('chats.downloadingEllipsis') : t('chats.download')}
            >
              <Icon name="download" size={16} />
            </Button>
          </figcaption>
        </figure>
        {downloadError ? <div className={styles.fileError}>{downloadError}</div> : null}
      </div>
    );
  }

  return (
    <div>
      <div className={styles.fileCard}>
        <span className={styles.fileIcon} aria-hidden>
          <Icon name="attach" size={16} />
        </span>
        <div className={styles.fileBody}>
          <span className={styles.fileName}>{filename}</span>
          <span className={styles.fileMeta}>
            {formatFileSize(fileAttachment.byte_size)} · {fileAttachment.mime_type ?? t('chats.unknownFileType')}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={() => void handleDownload()}
          disabled={!accessToken || isDownloading}
          aria-label={isDownloading ? t('chats.downloading') : t('chats.downloadFile')}
          title={isDownloading ? t('chats.downloadingEllipsis') : t('chats.download')}
        >
          <Icon name="download" size={16} />
        </Button>
      </div>
      {downloadError ? <div className={styles.fileError}>{downloadError}</div> : null}
    </div>
  );
}

function isImageAttachment(mimeType: string | null): boolean {
  return Boolean(mimeType && mimeType.toLowerCase().startsWith('image/'));
}

function cleanupRecorder(
  mediaRecorderRef: MutableRefObject<MediaRecorder | null>,
  mediaStreamRef: MutableRefObject<MediaStream | null>,
  options: { readonly cancel: boolean },
) {
  const recorder = mediaRecorderRef.current;
  if (recorder) {
    recorder.ondataavailable = null;
    recorder.onstop = null;
    recorder.onerror = null;
    if (options.cancel && recorder.state !== 'inactive') {
      recorder.stop();
    }
  }
  mediaRecorderRef.current = null;
  mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
  mediaStreamRef.current = null;
}

function resetVoiceDraft(
  currentDraft: VoiceDraft | null,
  setVoiceDraft: Dispatch<SetStateAction<VoiceDraft | null>>,
) {
  if (currentDraft) {
    URL.revokeObjectURL(currentDraft.url);
  }
  setVoiceDraft(null);
}

function getRecorderOptions(): MediaRecorderOptions | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  const preferredTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  const mimeType = preferredTypes.find((value) => MediaRecorder.isTypeSupported(value));
  return mimeType ? { mimeType } : undefined;
}

function buildVoiceFilename(mimeType: string): string {
  if (mimeType.includes('mp4')) {
    return 'voice-message.m4a';
  }
  if (mimeType.includes('mpeg')) {
    return 'voice-message.mp3';
  }
  return 'voice-message.webm';
}

function formatDuration(value: number | null): string {
  if (!value || value <= 0) return '0:00';
  const totalSeconds = Math.max(1, Math.round(value / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatFileSize(value: number | null): string {
  if (!value || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function parseContentDispositionFilename(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const match = /filename="([^"]+)"/i.exec(value);
  return match?.[1] ?? null;
}
